import React, { useState } from 'react';
import {
  Popover,
  PopoverTrigger,
  PopoverSurface,
  Button,
  Input,
  Field,
  Tooltip,
  Spinner,
  Text,
} from '@fluentui/react-components';
import { EditRegular } from '@fluentui/react-icons';
import { useAppRoles } from '../hooks/useAppRoles';
import { APP_ROLES } from '../constants/appRoles';
import { usePartnerAliases } from '../contexts/PartnerAliasContext';

interface PartnerAliasEditorProps {
  originalName: string | null | undefined;
}

// Administrator-only inline affordance to add / edit / remove the English alias for a partner. 
export const PartnerAliasEditor: React.FC<PartnerAliasEditorProps> = ({ originalName }) => {
  const { hasRole } = useAppRoles();
  const { getAlias, upsertAlias, deleteAlias } = usePartnerAliases();

  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!hasRole(APP_ROLES.administrator) || !originalName) return null;

  const existing = getAlias(originalName) ?? '';

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen) {
      setValue(existing);
      setError(null);
    }
  };

  const save = async () => {
    const trimmed = value.trim();
    if (!trimmed) {
      setError('Alias is required.');
      return;
    }
    if (trimmed === existing) {
      setOpen(false);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await upsertAlias(originalName, trimmed);
      setOpen(false);
    } catch {
      setError('Failed to save the alias. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await deleteAlias(originalName);
      setOpen(false);
    } catch {
      setError('Failed to remove the alias. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={(_e, data) => handleOpenChange(data.open)}
      trapFocus
      positioning="below-start"
    >
      <PopoverTrigger disableButtonEnhancement>
        <Tooltip content={existing ? 'Edit English alias' : 'Add English alias'} relationship="label">
          <Button
            appearance="subtle"
            size="small"
            icon={<EditRegular />}
            aria-label={existing ? 'Edit English alias' : 'Add English alias'}
            onClick={(e) => e.stopPropagation()}
          />
        </Tooltip>
      </PopoverTrigger>
      <PopoverSurface>
        <div
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !busy) {
              e.preventDefault();
              void save();
            }
          }}
          style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '280px' }}
        >
          <Field
            label="English alias"
            validationMessage={error ?? undefined}
            validationState={error ? 'error' : 'none'}
          >
            <Input
              value={value}
              onChange={(_e, data) => setValue(data.value)}
              placeholder="e.g. Shanghai Dongji Information Technology Co., Ltd"
              disabled={busy}
              autoFocus
            />
          </Field>
          <Text size={200} style={{ opacity: 0.7, wordBreak: 'break-word' }}>
            Original: {originalName}
          </Text>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            {existing && (
              <Button size="small" appearance="secondary" onClick={remove} disabled={busy}>
                Remove
              </Button>
            )}
            <Button size="small" appearance="primary" onClick={save} disabled={busy}>
              {busy ? <Spinner size="tiny" /> : 'Save'}
            </Button>
          </div>
        </div>
      </PopoverSurface>
    </Popover>
  );
};
